import React, { useEffect, useState } from 'react';

import Path from 'routes/Path';
import { getConfig } from 'functions/getConfig';
import { useLanguageVersions } from 'hooks/useConfig';
import { useLoadFreecodeTrialTest } from 'hooks/useFreecode';
import { FreecodeLoading } from 'components/FreecodeLoading/FreecodeLoading';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContext } from 'hooks/useContext';
import FreecodeEditorComponents from 'components/FreecodePage/FreecodeEditorComponents';
import PageUnderConstruction from 'components/FreecodePage/PageUnderConstruction';
import ConfirmModal from 'components/Modals/confirmModal';

const HEADER_LINKS = [
  { link: Path.candidateTicketsPage, label: 'Your Tests' },
  { link: `${getConfig().REDIRECTION_ROOT_URL}/trialTest`, label: 'Old Editor' },
];

const submitModalMessage =
  'Are you sure you want to submit your solution? You can repeat the Trial Test as many times as you like, however when submitting during a real test you will not be able to continue working on your code.';

export default function FreecodeTestTrial() {
  const [loadingDone, setLoadingDone] = useState(false);
  const [loadingError, setLoadingError] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [submittedText, setSubmittedText] = useState(undefined);
  const [errorLinkText, setErrorLinkText] = useState('Go to home page');
  const [errorLink, setErrorLink] = useState(Path.landingPage);

  const { setTest, setTestImpl, setLanguageData, setLanguage, setSubmit, setCode, setTicket } = useFreecodeContext();

  const [languageVersions /** ignore next */, , languageVersionsError] = useLanguageVersions();
  const [freecodeResponse /** ignore next */, , freecodeError] = useLoadFreecodeTrialTest();

  useEffect(() => {
    const timeLimit = 30;
    setTicket({
      timeLimit,
      startedAt: new Date(),
      deadlineAt: new Date(Date.now() + timeLimit * 60 * 1000),
    });
    setSubmit(() => () => setSubmitting(true));
  }, [setSubmit, setTicket]);

  useEffect(() => {
    if (!languageVersions || !freecodeResponse) return;
    // When loading is done destructure the data
    try {
      let { test, testImpl } = freecodeResponse;
      let allowedLanguages = languageVersions[testImpl.executionVersion];

      const foundLanguage =
        localStorage.getItem(`fsEditor-${test.category}-${test._id}-language`) ||
        (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);

      setTest(test);
      setTestImpl(testImpl);
      setLanguageData(allowedLanguages);
      setLanguage(foundLanguage);
      setCode(testImpl.generated?.skeletons?.[foundLanguage] || 'Code not found...');
      setLoadingDone(true);
    } catch (error) {
      setLoadingError('Could not unpack freecode data');
    }
  }, [languageVersions, freecodeResponse, setLanguageData, setLanguage, setCode, setTest, setTestImpl]);

  const performTrialSubmit = () => {
    setErrorLink(Path.candidateTicketsPage);
    setErrorLinkText('Go to tests');
    setSubmitting(false);
    setLoadingDone(false);
    setSubmittedText('Now you can close this tab and start taking your tests');
  };

  return (
    <FreecodePage headerLinks={HEADER_LINKS}>
      <FreecodeLoading
        loading={!loadingDone}
        errors={[languageVersionsError, freecodeError, loadingError, submittedText]}
        errorLinkText={errorLinkText}
        errorLink={errorLink}
      />
      <PageUnderConstruction />
      {loadingDone && <FreecodeEditorComponents />}
      {submitting && (
        <ConfirmModal
          title="Submit"
          text={submitModalMessage}
          onConfirm={performTrialSubmit}
          onCancel={() => setSubmitting(false)}
        />
      )}
    </FreecodePage>
  );
}
